/* eslint-disable @typescript-eslint/no-explicit-any */
const RECAPTCHA_SCRIPT_BASE = "https://www.google.com/recaptcha/enterprise.js";

let scriptLoadPromise: Promise<void> | null = null;
let loadedLanguage: string | undefined = undefined;

function getRecaptchaApi(): any {
  const w = window as any;
  return w.grecaptcha && w.grecaptcha.enterprise;
}

function cleanupRecaptchaState(): void {
  document.querySelectorAll(`script[src^="${RECAPTCHA_SCRIPT_BASE}"]`).forEach((el) => el.remove());
  const w = window as any;
  delete w.grecaptcha;
  delete w.___grecaptcha_cfg;
}

export function loadRecaptchaScript(siteKey: string, language?: string): Promise<void> {
  // Return cached promise only if the requested language matches what was already loaded
  if (scriptLoadPromise && loadedLanguage === language) return scriptLoadPromise;
  // Language changed → reload script with new hl param so the badge follows the app language
  if (scriptLoadPromise) cleanupRecaptchaState();
  loadedLanguage = language;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const api = getRecaptchaApi();
    if (api && api.execute) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    const langParam = language ? `&hl=${encodeURIComponent(language)}` : "";
    script.src = `${RECAPTCHA_SCRIPT_BASE}?render=${siteKey}${langParam}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      const apiAfterLoad = getRecaptchaApi();
      if (apiAfterLoad) {
        apiAfterLoad.ready(() => resolve());
      } else {
        resolve();
      }
    };

    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load reCAPTCHA Enterprise script"));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export function executeV3(siteKey: string, action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      resolve("");
      return;
    }

    const api = getRecaptchaApi();
    if (!api || !api.execute) {
      resolve("");
      return;
    }

    api
      .execute(siteKey, { action })
      .then((token: string) => resolve(token))
      .catch((err: any) => reject(err));
  });
}

export interface RenderV2Options {
  onVerify?: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
  action?: string;
}

export function renderV2(container: HTMLElement, siteKey: string, opts: RenderV2Options = {}): number {
  const api = getRecaptchaApi();
  if (!api || !api.render) {
    throw new Error("grecaptcha.enterprise not available — call loadRecaptchaScript() first");
  }

  const params: Record<string, unknown> = {
    sitekey: siteKey,
    callback: opts.onVerify,
    "expired-callback": opts.onExpired,
    "error-callback": opts.onError,
  };
  if (opts.action) {
    params.action = opts.action;
  }

  return api.render(container, params);
}

export function resetV2(widgetId: number): void {
  const api = getRecaptchaApi();
  if (api && api.reset) {
    api.reset(widgetId);
  }
}
